import { BookingTeamEmailButton } from './BookingTeamEmailButton'
/**
 * El equipo de la agenda: quiénes atienden.
 *
 * Cada persona es un «recurso» del worker —con su horario, sus días libres y
 * su calendario conectado—. Aquí se da de alta, se le pone cara y puesto (lo
 * que enseña la página de reserva: «Ana · Estilista»), se aparta o se quita.
 * El horario NO se edita aquí: vive en el paso Horario de cada servicio, que ya
 * tiene el editor completo y ahora deja elegir de quién es.
 *
 * Con una sola persona la página de reserva sigue siendo la de siempre; el
 * selector «Con quién» sólo aparece cuando un servicio lo dan varias.
 */
import { useState } from 'react'
import { createPortal } from 'react-dom'
import { IconCamera, IconPhoto, IconPlus, IconTrash, IconUserPlus, IconX } from '@tabler/icons-react'
import { InfoHint } from '../../SettingsControls'
import { useI18n } from '../../../lib/i18n'
import { ImageLibraryPickerDialog } from '../../ImageLibraryPickerDialog'
import { bookingError } from './bookingShared'
import {
  createBookingResource,
  deleteBookingResource,
  saveBookingSchedule,
  updateBookingResource,
  type BookingResource,
} from '../../../lib/api'


function initials(name: string): string {
  return name.trim().split(/\s+/).slice(0, 2).map((part) => part[0] ?? '').join('').toUpperCase()
}

export function BookingTeamDialog({ projectId, token, resources, onClose, onReload }: {
  projectId: string
  token: string
  resources: BookingResource[]
  onClose: () => void
  /** Tras cualquier cambio: el equipo se vuelve a pedir entero. */
  onReload: () => Promise<void>
}) {
  const { t } = useI18n()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [newName, setNewName] = useState('')
  const [newRole, setNewRole] = useState('')
  /* De quién se está eligiendo la foto. */
  const [photoFor, setPhotoFor] = useState<string | null>(null)
  /* Lo tecleado en cada fila, hasta que se sale del campo y se guarda. */
  const [drafts, setDrafts] = useState<Record<string, { name?: string; role?: string; email?: string }>>({})
  const activeCount = resources.filter((row) => row.active).length

  const run = async (action: () => Promise<unknown>) => {
    setBusy(true); setError('')
    try {
      await action()
      await onReload()
      return true
    } catch (failure) {
      setError(bookingError(failure))
      return false
    } finally {
      setBusy(false)
    }
  }

  const saveField = (member: BookingResource, field: 'name' | 'role' | 'email') => {
    const value = drafts[member.id]?.[field]
    if (value === undefined) return
    const trimmed = value.trim()
    setDrafts((current) => ({ ...current, [member.id]: { ...current[member.id], [field]: undefined } }))
    /* Un nombre vacío no se guarda: se vuelve al que tenía. */
    if ((field === 'name' && !trimmed) || trimmed === (member[field] ?? '')) return
    /* Un correo mal escrito tampoco: se avisa y se queda el que había. */
    if (field === 'email' && trimmed && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) { setError(t('bookings.team.emailInvalid')); return }
    void run(() => updateBookingResource(projectId, member.id, { [field]: trimmed }, token))
  }

  const add = () => {
    const name = newName.trim()
    if (!name) return
    void run(async () => {
      const created = await createBookingResource(projectId, { name, role: newRole.trim() }, token)
      /* Nace con la semana del primero del equipo, no vacía: sin horario no
         saldría ni un hueco suyo, y casi siempre el equipo abre las mismas
         horas. Se ajusta luego en el paso Horario de sus servicios. */
      const base = resources[0]
      if (base?.rules.length) {
        await saveBookingSchedule(projectId, created.id, { rules: base.rules, exceptions: [] }, token)
      }
      setNewName(''); setNewRole('')
      setAdding(false)
    })
  }

  const [adding, setAdding] = useState(resources.length <= 1)

  return createPortal(
    <div
      className="dash-modal-backdrop dash-new-booking-backdrop"
      onPointerDown={(event) => { if (event.target === event.currentTarget && !busy) onClose() }}
    >
      <section className="dash-new-booking-dialog dash-team-dialog" role="dialog" aria-modal="true" aria-labelledby="team-title">
        <header className="dash-new-booking-head">
          <div className="dash-new-booking-head-copy">
            <h2 id="team-title">
              {t('bookings.team.title')}
              <span className="dash-team-count">{resources.length}</span>
              {/* Dónde se pone el horario y qué pasa con quien está oculto: lo
                  que antes era un párrafo al pie, en su «i». */}
              <InfoHint text={t('bookings.team.scheduleNote')} />
            </h2>
            <p>{t('bookings.team.lead')}</p>
          </div>
          <button type="button" className="dash-create-close" aria-label={t('bookings.new.close')} disabled={busy} onClick={onClose}>
            <IconX size={16} />
          </button>
        </header>

        <div className="dash-team-body">
          {/*
            Una tarjeta por persona, como un contacto: la cara, el nombre y su
            puesto se editan donde se leen —sin rótulos de formulario encima—,
            y a la derecha si está en la página de reserva.
          */}
          <ul className="dash-team-list">
            {resources.map((member, index) => (
              <li key={member.id} className={`dash-team-card${member.active ? '' : ' is-inactive'}`}>
                <button
                  type="button"
                  className="dash-team-photo"
                  aria-label={t('bookings.team.photo', { name: member.name })}
                  title={t('bookings.team.photo', { name: member.name })}
                  disabled={busy}
                  onClick={() => setPhotoFor(member.id)}
                >
                  {member.photoUrl
                    ? <img src={member.photoUrl} alt="" />
                    : <span>{initials(member.name) || <IconPhoto size={16} />}</span>}
                  <i aria-hidden="true"><IconCamera size={13} /></i>
                </button>
                <div className="dash-team-who">
                  <input
                    className="dash-team-name"
                    aria-label={t('bookings.team.name')}
                    value={drafts[member.id]?.name ?? member.name}
                    maxLength={120}
                    disabled={busy}
                    onChange={(event) => setDrafts((current) => ({ ...current, [member.id]: { ...current[member.id], name: event.target.value } }))}
                    onBlur={() => saveField(member, 'name')}
                    onKeyDown={(event) => { if (event.key === 'Enter') event.currentTarget.blur() }}
                  />
                  <input
                    className="dash-team-role"
                    aria-label={t('bookings.team.role')}
                    value={drafts[member.id]?.role ?? member.role ?? ''}
                    maxLength={80}
                    placeholder={t('bookings.team.addRole')}
                    disabled={busy}
                    onChange={(event) => setDrafts((current) => ({ ...current, [member.id]: { ...current[member.id], role: event.target.value } }))}
                    onBlur={() => saveField(member, 'role')}
                    onKeyDown={(event) => { if (event.key === 'Enter') event.currentTarget.blur() }}
                  />
                </div>
                <div className="dash-team-side">
                  <BookingTeamEmailButton
                    name={member.name}
                    email={member.email ?? ''}
                    busy={busy}
                    onSave={(email) => run(() => updateBookingResource(projectId, member.id, { email }, token))}
                  />
                  <span className={`dash-team-state${member.active ? ' is-on' : ''}`}>{t(member.active ? 'bookings.team.bookable' : 'bookings.team.hidden')}</span>
                  <button
                    type="button"
                    className={`dash-general-toggle${member.active ? ' is-on' : ''}`}
                    role="switch"
                    aria-checked={member.active}
                    aria-label={t('bookings.team.active')}
                    title={t(member.active ? 'bookings.team.bookable' : 'bookings.team.hidden')}
                    /* Siempre alguien activo: sin nadie, ningún servicio se puede reservar. */
                    disabled={busy || (member.active && activeCount <= 1)}
                    onClick={() => void run(() => updateBookingResource(projectId, member.id, { active: !member.active }, token))}
                  >
                    <span />
                  </button>
                  {/* El primero no se quita: es el de los servicios sin equipo. */}
                  {index > 0 ? (
                    <button
                      type="button"
                      className="dash-team-remove"
                      aria-label={t('bookings.team.remove', { name: member.name })}
                      title={t('bookings.team.remove', { name: member.name })}
                      disabled={busy}
                      onClick={() => void run(() => deleteBookingResource(projectId, member.id, token))}
                    ><IconTrash size={15} /></button>
                  ) : <span className="dash-team-remove" aria-hidden="true" />}
                </div>
              </li>
            ))}
          </ul>

          {/* Agregar: un renglón discreto que se abre en la misma tarjeta. */}
          {adding ? (
            <form className="dash-team-card dash-team-add" onSubmit={(event) => { event.preventDefault(); add() }}>
              <span className="dash-team-photo is-new" aria-hidden="true"><span><IconUserPlus size={17} /></span></span>
              <div className="dash-team-who">
                <input className="dash-team-name" autoFocus value={newName} maxLength={120} placeholder={t('bookings.team.newNamePlaceholder')} aria-label={t('bookings.team.name')} disabled={busy} onChange={(event) => setNewName(event.target.value)} />
                <input className="dash-team-role" value={newRole} maxLength={80} placeholder={t('bookings.team.rolePlaceholder')} aria-label={t('bookings.team.role')} disabled={busy} onChange={(event) => setNewRole(event.target.value)} />
              </div>
              <div className="dash-team-side">
                {resources.length > 1 ? <button type="button" className="dash-ghost-btn" disabled={busy} onClick={() => { setAdding(false); setNewName(''); setNewRole('') }}>{t('bookings.team.cancel')}</button> : null}
                <button type="submit" className="dash-primary-btn" disabled={busy || !newName.trim()}>{t('bookings.team.add')}</button>
              </div>
            </form>
          ) : (
            <button type="button" className="dash-team-add-row" disabled={busy} onClick={() => setAdding(true)}>
              <IconPlus size={15} aria-hidden="true" /> {t('bookings.team.newName')}
            </button>
          )}


          {error ? <p className="dash-team-error" role="alert">{error}</p> : null}
        </div>
      </section>

      {photoFor ? (
        <ImageLibraryPickerDialog
          token={token}
          projectId={projectId}
          onSelect={(url) => {
            const id = photoFor
            setPhotoFor(null)
            void run(() => updateBookingResource(projectId, id, { photoUrl: url }, token))
          }}
          onClose={() => setPhotoFor(null)}
        />
      ) : null}
    </div>,
    document.body,
  )
}
